import React, { ReactElement, useEffect } from "react";
import { HighlightText } from "../form/HighlightText";

export interface IComboBoxOption {
    _id: string;
    name: string;
    text: string;
}

interface IComboBox {
    options: IComboBoxOption[];
    placeholder?: string;
    className?: string;
    defaultValue?: string | IComboBoxOption;
    maxListCount?: number;
    disabled?: boolean;
    onOptionSelect?: (selectedOption: IComboBoxOption) => void;
    onChange?: (text: string) => void;
}

export function ComboBox({
    options,
    defaultValue = "",
    className = "",
    placeholder = "Search",
    maxListCount = 50,
    disabled = false,
    onChange,
    onOptionSelect
}: IComboBox): ReactElement {
    const [query, setQuery] = React.useState<string>("");
    const [open, setOpen] = React.useState(false);

    useEffect(() => {
        const defaultQuery = defaultValue ? (typeof defaultValue === "string" ? defaultValue : defaultValue.text) : "";
        setQuery(defaultQuery);
    }, [defaultValue]);

    const filterSuggestions = (value: string) => {
        if (!value) {
            return options.slice(0, 30);
        }
        const escapeRegexCharacters = (str: string) => str.replace(/[.*+?^${}()|[\]\\@]/g, "\\$&");
        const escapedValue = escapeRegexCharacters(value?.trim());
        if (escapedValue === "") {
            return [];
        }
        const regex = new RegExp(escapedValue, "i");
        const res: { value; text: string; sortOrder: number }[] = [];
        for (let i = 0; i < options.length; i++) {
            const value = options[i];
            const text: string = options[i].text;
            if (text) {
                const sortOrder = text.search(regex);
                if (sortOrder >= 0) {
                    res.push({ value, text, sortOrder });
                }
            }
        }
        res.sort((d1, d2) => {
            const c = d1.sortOrder - d2.sortOrder;
            return c !== 0 ? c : d1.text < d2.text ? -1 : d1.text > d2.text ? 1 : 0;
        });
        const result = res.map((d) => d.value);
        if (result.length > maxListCount) {
            return result.slice(0, maxListCount);
        }
        return result;
    };

    const filteredSuggestions = filterSuggestions(query);

    return (
        <div
            className="combo-box"
            onBlur={() => {
                setOpen(false);
            }}
        >
            <div>
                <input
                    type="text"
                    className={`form-input ${className}`}
                    style={{ boxShadow: "none" }}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (onChange) {
                            onChange(e.target.value);
                        }
                    }}
                    placeholder={placeholder}
                    onFocus={() => setOpen(true)}
                    disabled={disabled}
                />
            </div>
            {open && filteredSuggestions.length > 0 ? (
                <ul
                    className="suggestions-list"
                    style={{
                        overflowX: "hidden",
                        paddingRight: 6,
                        border: "1px solid #ccc",
                        boxShadow: "none"
                    }}
                >
                    {filteredSuggestions.map((suggestion, i) => {
                        return (
                            <a
                                href="#"
                                style={{ color: "#333" }}
                                onMouseDown={() => {
                                    setQuery(suggestion.name);
                                    if (onOptionSelect) {
                                        onOptionSelect(suggestion || null);
                                    }
                                }}
                            >
                                <li key={i} className="suggestion-item" style={{ padding: "6px 8px", textWrap: "wrap" }}>
                                    {<HighlightText text={suggestion.text} highlight={query} />}
                                </li>
                            </a>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}
